import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldAlert, ShieldCheck, CreditCard, Save, Pencil, X, ExternalLink, FileSignature, Lock, ScanText,
} from "lucide-react";
import api from "../../lib/api";
import FoldBlock from "./FoldBlock";
import DatePickerInput from "../DatePickerInput";
import VoiceTextarea from "../VoiceTextarea";

/* Wave 5 — block toggles + credit terms + link to SA account application.
 * Mounted inside the Account tab on the customer detail page. */
const PAYMENT_TERMS = [
  { v: "cod", label: "COD" },
  { v: "7d", label: "7 days" },
  { v: "14d", label: "14 days" },
  { v: "30d", label: "30 days" },
  { v: "60d", label: "60 days" },
  { v: "custom", label: "Custom" },
];

export default function AccountManagementBlock({ customer, canEdit, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    portal_access_blocked: !!customer.portal_access_blocked,
    new_orders_blocked: !!customer.new_orders_blocked,
    block_reason: customer.block_reason || "",
    payment_terms: customer.payment_terms || "",
    payment_terms_custom: customer.payment_terms_custom || "",
    credit_limit: customer.credit_limit ?? "",
    credit_balance: customer.credit_balance ?? "",
    credit_check_date: customer.credit_check_date || "",
    credit_check_result: customer.credit_check_result || "",
    credit_notes: customer.credit_notes || "",
  });

  const isBlocked = customer.portal_access_blocked || customer.new_orders_blocked;
  const accountStatus = customer.account_status || "none";

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const patch = {
        ...form,
        credit_limit: form.credit_limit === "" ? null : Number(form.credit_limit),
        credit_balance: form.credit_balance === "" ? null : Number(form.credit_balance),
      };
      await api.patch(`/customers/${customer.id}/account`, patch);
      setEdit(false);
      onChanged?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const publicLink = `${window.location.origin}/apply/account?customer_id=${customer.id}`;

  return (
    <FoldBlock
      icon={ShieldCheck}
      title="Account management"
      defaultOpen={false}
      count={isBlocked ? "BLOCKED" : accountStatus !== "none" ? accountStatus.replace("_", " ") : undefined}
      countAccent={isBlocked ? "#F43F5E" : accountStatus === "approved" ? "#10B981" : "#647087"}
      alwaysRenderChildren
      testId="account-mgmt-block"
    >
      {!edit ? (
        <div className="space-y-3" data-testid="account-mgmt-readonly">
          {/* Status banner */}
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm border ${
              isBlocked
                ? "border-rose-500/40 bg-rose-500/10 text-rose-200"
                : "border-emerald-500/30 bg-emerald-500/[0.04] text-emerald-200"
            }`}
          >
            {isBlocked ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
            <div className="flex-1">
              {isBlocked ? (
                <>
                  Account blocked
                  {customer.portal_access_blocked && customer.new_orders_blocked
                    ? " — portal access + new orders denied"
                    : customer.portal_access_blocked
                    ? " — portal login denied"
                    : " — new orders/quotes denied"}
                  {customer.block_reason && (
                    <div className="text-[11px] text-[#a0aabf] italic mt-0.5">
                      Reason: {customer.block_reason}
                    </div>
                  )}
                </>
              ) : (
                "Account active — portal access and new orders are unrestricted."
              )}
            </div>
          </div>

          {/* Terms summary */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Cell label="Payment terms" value={
              customer.payment_terms === "custom"
                ? customer.payment_terms_custom || "Custom"
                : (PAYMENT_TERMS.find((p) => p.v === customer.payment_terms)?.label || "—")
            } />
            <Cell label="Credit limit" value={
              customer.credit_limit != null ? `R ${Number(customer.credit_limit).toLocaleString('en-GB')}` : "—"
            } />
            <Cell label="Balance owing" value={
              customer.credit_balance != null ? `R ${Number(customer.credit_balance).toLocaleString('en-GB')}` : "—"
            } />
            <Cell label="Last credit check" value={customer.credit_check_date || "—"} />
            <Cell label="Result" value={customer.credit_check_result || "—"} />
            <Cell label="Account status" value={accountStatus.replace("_", " ")} />
          </div>

          {customer.credit_notes && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Credit notes (admin-only)</div>
              <div className="text-xs text-[#dde3ec] whitespace-pre-wrap leading-relaxed">{customer.credit_notes}</div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
            {canEdit && (
              <button type="button" onClick={() => setEdit(true)} className="hv-btn hv-btn-ghost text-xs" data-testid="account-mgmt-edit">
                <Pencil className="w-3.5 h-3.5" /> Edit terms & blocks
              </button>
            )}
            {canEdit && (
              <Link
                to={`/ocr/invoices?customer_id=${customer.id}`}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="account-mgmt-scan-invoice"
                title="Scan a supplier invoice and auto-create equipment for this customer"
              >
                <ScanText className="w-3.5 h-3.5" /> Scan invoice
              </Link>
            )}
            {canEdit && (
              <a
                href={publicLink}
                target="_blank"
                rel="noreferrer"
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="account-mgmt-apply-link"
              >
                <FileSignature className="w-3.5 h-3.5" /> Open application form
              </a>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(publicLink)}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="account-mgmt-copy-link"
                title="Copy public application link"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Copy public link
              </button>
            )}
            <Link
              to="/account-applications"
              className="hv-btn hv-btn-ghost text-xs ml-auto"
              data-testid="account-mgmt-review-link"
            >
              <CreditCard className="w-3.5 h-3.5" /> Review queue
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="account-mgmt-edit-form">
          {/* Block toggles */}
          <div className="space-y-2 border border-rose-500/30 rounded-md p-3 bg-rose-500/[0.04]">
            <div className="text-[10px] uppercase tracking-wider text-rose-300 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Block toggles
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.portal_access_blocked}
                onChange={(e) => setForm({ ...form, portal_access_blocked: e.target.checked })}
                data-testid="block-portal-toggle"
              />
              <span>Block portal access (hard block — can't log in)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.new_orders_blocked}
                onChange={(e) => setForm({ ...form, new_orders_blocked: e.target.checked })}
                data-testid="block-orders-toggle"
              />
              <span>Block new orders / quotes (soft block)</span>
            </label>
            <input
              value={form.block_reason}
              onChange={(e) => setForm({ ...form, block_reason: e.target.value })}
              placeholder="Block reason (visible internally only)"
              className="hv-input text-sm w-full"
              data-testid="block-reason-input"
            />
          </div>

          {/* Terms */}
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Payment terms</label>
              <select
                value={form.payment_terms}
                onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                className="hv-input mt-1 text-sm"
                data-testid="payment-terms-select"
              >
                <option value="">— None —</option>
                {PAYMENT_TERMS.map((p) => (
                  <option key={p.v} value={p.v}>{p.label}</option>
                ))}
              </select>
            </div>
            {form.payment_terms === "custom" && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#647087]">Custom terms</label>
                <input
                  value={form.payment_terms_custom}
                  onChange={(e) => setForm({ ...form, payment_terms_custom: e.target.value })}
                  className="hv-input mt-1 text-sm"
                  placeholder="e.g. 45 days end of month"
                />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Credit limit (R)</label>
              <input
                type="number"
                value={form.credit_limit}
                onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                className="hv-input mt-1 text-sm"
                data-testid="credit-limit-input"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Balance owing (R)</label>
              <input
                type="number"
                value={form.credit_balance}
                onChange={(e) => setForm({ ...form, credit_balance: e.target.value })}
                className="hv-input mt-1 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Credit check date</label>
              <DatePickerInput
                value={form.credit_check_date || ""}
                onChange={(v) => setForm({ ...form, credit_check_date: v })}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#647087]">Result</label>
              <select
                value={form.credit_check_result}
                onChange={(e) => setForm({ ...form, credit_check_result: e.target.value })}
                className="hv-input mt-1 text-sm"
              >
                <option value="">—</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">Internal credit notes</label>
            <VoiceTextarea
              value={form.credit_notes}
              onChange={(v) => setForm({...form, credit_notes: v})}
             context="note" />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-white/5">
            {err && <div className="text-xs text-rose-300 mr-auto self-center">{err}</div>}
            <button type="button" onClick={() => setEdit(false)} className="hv-btn hv-btn-ghost text-xs">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="account-mgmt-save"
            >
              <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </FoldBlock>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-sm text-[#dde3ec]">{value || "—"}</div>
    </div>
  );
}
